/**
 * Sales call REST client.
 *
 * Its own module beside `api.ts` and `crm-api.ts`, for the reason given at the
 * top of that second one: three products behind one origin, and one import per
 * screen keeps it obvious which surface a page is talking to. The transport is
 * shared, so cookies, the `/api` prefix and error mapping cannot drift.
 */

import { request } from "./api";
import type {
  CallListParams,
  CreateCallInput,
  CreateMeetingInput,
  MeetingCounts,
  MeetingListParams,
  SalesReport,
  SalesBoard,
  SalesCall,
  SalesCallDetail,
  SalesCallPage,
  SalesConfig,
  SalesMeetingDetail,
  SalesMeetingPage,
  SalesMeeting,
  SalesScore,
  SalesGroup,
  OverdueRules,
} from "./sales-types";

/** Drops empty values so URLs stay readable and cache keys stay stable. */
function qs(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "" || value === false) continue;
    search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : "";
}

const send = (body: unknown, method = "POST"): RequestInit => ({
  method,
  body: JSON.stringify(body),
});

export const salesApi = {
  // ── The pool ──────────────────────────────────────────────────────────────

  calls: (params: CallListParams = {}) =>
    request<SalesCallPage>(
      `/sales/calls${qs({ ...params } as Record<string, string | number | boolean | undefined>)}`,
    ),

  call: (id: number) => request<SalesCallDetail>(`/sales/calls/${id}`),

  create: (input: CreateCallInput) =>
    request<SalesCallDetail>("/sales/calls", send(input)),

  update: (id: number, patch: Partial<CreateCallInput>) =>
    request<SalesCallDetail>(`/sales/calls/${id}`, send(patch, "PATCH")),

  /** 409 when somebody else got there first; the message names them. */
  claim: (id: number) =>
    request<SalesCallDetail>(`/sales/calls/${id}/claim`, { method: "POST" }),

  release: (id: number) =>
    request<SalesCallDetail>(`/sales/calls/${id}/release`, { method: "POST" }),

  /** Manager only. `null` puts the call back in the pool. */
  assign: (id: number, userId: number | null) =>
    request<SalesCallDetail>(`/sales/calls/${id}/assign`, send({ userId })),

  /** Manager only: move it into, or out of, the front of the worklist. */
  pin: (id: number, pinned: boolean) =>
    request<SalesCallDetail>(`/sales/calls/${id}/pin`, send({ pinned })),

  logAttempt: (
    id: number,
    input: {
      outcome: string;
      note?: string | null;
      callbackAt?: string | null;
      occurredAt?: string;
    },
  ) => request<SalesCallDetail>(`/sales/calls/${id}/attempts`, send(input)),

  /**
   * Raise the next call after this one: Medium priority, "Follow-up", claimed
   * by you. Answers the *new* call.
   */
  followUp: (id: number, input: { note?: string | null; dueAt?: string | null }) =>
    request<SalesCallDetail>(`/sales/calls/${id}/follow-up`, send(input)),

  close: (
    id: number,
    input: {
      scoreId: number;
      closingNotes?: string | null;
      /** Log the conversation this close ends. See `CloseForm`. */
      spoke?: boolean;
    },
  ) => request<SalesCallDetail>(`/sales/calls/${id}/close`, send(input)),

  reopen: (id: number) =>
    request<SalesCallDetail>(`/sales/calls/${id}/reopen`, { method: "POST" }),

  /** Manager only, and refused once anybody has actually rung. */
  remove: (id: number) =>
    request<void>(`/sales/calls/${id}`, { method: "DELETE" }),

  // ── The landing view ──────────────────────────────────────────────────────

  board: () => request<SalesBoard>("/sales/board"),

  /** Every call ever raised against one CRM account, newest first. */
  forCompany: (companyId: number) =>
    request<SalesCall[]>(`/sales/companies/${companyId}/calls`),

  /** The manager's dashboard for one period. */
  report: (range: string, from?: string, to?: string) =>
    request<SalesReport>(`/sales/report${qs({ range, from, to })}`),

  /*
   * ── The diary ─────────────────────────────────────────────────────────────
   *
   * Meetings are their own product in the rail and live at `/meetings` in the
   * front end, but their API stays under `/sales` and so do their tables. That
   * is not an oversight: it is one NestJS module, it shares the calls' score
   * list, and a meeting raised off a call links back to it. Where something
   * sits in the navigation is a question about who works it; where it sits in
   * the API is a question about what it belongs to, and the two answers are
   * allowed to differ.
   */

  meetings: (params: MeetingListParams = {}) =>
    request<SalesMeetingPage>(
      `/sales/meetings${qs({ ...params } as Record<string, string | number | boolean | undefined>)}`,
    ),

  meeting: (id: number) => request<SalesMeetingDetail>(`/sales/meetings/${id}`),

  /** The signed-in rep's own three numbers, for the sidebar. */
  meetingCounts: () => request<MeetingCounts>("/sales/meetings/counts"),

  createMeeting: (input: CreateMeetingInput) =>
    request<SalesMeetingDetail>("/sales/meetings", send(input)),

  /** Reschedule, relocate, hand over. A move is recorded in the meeting's log. */
  updateMeeting: (id: number, patch: Partial<CreateMeetingInput>) =>
    request<SalesMeetingDetail>(`/sales/meetings/${id}`, send(patch, "PATCH")),

  addMeetingNote: (id: number, body: string) =>
    request<SalesMeetingDetail>(`/sales/meetings/${id}/notes`, send({ body })),

  /** Replace the notes document. The editor autosaves through this. */
  saveMeetingNotes: (id: number, html: string) =>
    request<{ notesUpdatedAt: string; notesUpdatedByName: string; hasNotes: boolean }>(
      `/sales/meetings/${id}/notes-doc`,
      send({ html }, "PUT"),
    ),

  /** Keep your unsent follow-up. The composer saves through this as you type. */
  saveMeetingFollowUpDraft: (
    id: number,
    input: {
      to: { email: string; name?: string }[];
      cc: { email: string; name?: string }[];
      subject: string;
      html: string;
    },
  ) =>
    request<{ updatedAt: string }>(`/sales/meetings/${id}/follow-up-draft`, send(input, "PUT")),

  discardMeetingFollowUpDraft: (id: number) =>
    request<void>(`/sales/meetings/${id}/follow-up-draft`, { method: "DELETE" }),

  /** Send a follow-up from your own Gmail. Logged on the company. */
  sendMeetingFollowUp: (
    id: number,
    input: {
      to: { email: string; name?: string }[];
      cc?: { email: string; name?: string }[];
      subject: string;
      html: string;
    },
  ) =>
    request<SalesMeetingDetail>(`/sales/meetings/${id}/follow-ups`, send(input)),

  /**
   * Raise a Lane task out of the meeting.
   *
   * One request: the server creates the task through the same service the CRM
   * uses and logs it against the meeting, so there is no way to end up with one
   * without the other.
   */
  addMeetingTask: (
    id: number,
    input: { title: string; assigneeIds?: string[]; dueAt?: string | null },
  ) => request<SalesMeetingDetail>(`/sales/meetings/${id}/tasks`, send(input)),

  /** The only ending a meeting has. The reason goes into its log. */
  cancelMeeting: (id: number, reason?: string | null) =>
    request<SalesMeetingDetail>(
      `/sales/meetings/${id}/cancel`,
      send({ reason: reason ?? null }),
    ),

  restoreMeeting: (id: number) =>
    request<SalesMeetingDetail>(`/sales/meetings/${id}/restore`, {
      method: "POST",
    }),

  /** Manager only, and refused once anything has been written against it. */
  removeMeeting: (id: number) =>
    request<void>(`/sales/meetings/${id}`, { method: "DELETE" }),

  /** Every meeting ever held with one CRM account, newest first. */
  meetingsForCompany: (companyId: number) =>
    request<SalesMeeting[]>(`/sales/companies/${companyId}/meetings`),

  // ── Configuration ─────────────────────────────────────────────────────────

  config: () => request<SalesConfig>("/sales/config"),

  createGroup: (input: {
    name: string;
    type: string;
    overdueNoAttemptHours?: number | null;
    overdueStaleDays?: number | null;
  }) => request<SalesGroup>("/sales/groups", send(input)),

  updateGroup: (id: number, patch: Partial<SalesGroup>) =>
    request<SalesGroup>(`/sales/groups/${id}`, send(patch, "PATCH")),

  createScore: (input: { label: string; tone?: string; position?: number }) =>
    request<SalesScore>("/sales/scores", send(input)),

  updateScore: (id: number, patch: Partial<SalesScore>) =>
    request<SalesScore>(`/sales/scores/${id}`, send(patch, "PATCH")),

  setRules: (patch: {
    noAttemptHours?: number;
    staleDays?: number;
    /** null switches the third rule off. */
    daysSinceRaised?: number | null;
  }) => request<OverdueRules>("/sales/rules", send(patch, "PATCH")),
};

export type { SalesCall };
